import styled from "@emotion/styled";
import { SubscriptionFlowChangeEmailForm } from "./SubscriptionFlowChangeEmailForm";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import {
  SubscriptionFlowChangeEmailSchema,
  SUBSCRIPTION_FLOW_CHANGE_EMAIL_SCHEMA_DEFAULT_VALUES,
  subscriptionFlowChangeEmailFormSchema,
  SUBSCRIPTION_FLOW_TOKEN_VALIDATION_SCHEMA_DEFAULT_VALUES,
  subscriptionFlowTokenValidationFormSchema,
  SubscriptionFlowTokenValidationSchema,
} from "./subscription-flow-change-email-form-helpers";
import { SubscriptionFlowTokenValidationForm } from "./SubscriptionFlowTokenValidationForm";
import { useStartChangeSubscriptionEmail } from "@queries/subscription-flow/useStartChangeSubscriptionEmail";
import { useCallback } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useConfirmChangeSubscriptionEmail } from "@queries/subscription-flow/useConfirmChangeSubscriptionEmail";

const CustomDialog = styled(Dialog)`
  transition: all 0.5s ease;

  & .MuiDialog-paper {
    /* padding: 1rem; */
    background-color: ${(p) => p.theme.colors.background};
    transition: all 0.5s ease;

    ${(p) => p.theme.media.mobileDown} {
      padding: 0rem;
    }
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
  subscriptionId: string;
  currentEmail: string;
};

export const SubscriptionFlowChangeEmail = (props: Props) => {
  const { open, onClose, subscriptionId, currentEmail } = props;

  const changeSubscriptionEmailFormMethods =
    useForm<SubscriptionFlowChangeEmailSchema>({
      defaultValues: {
        ...SUBSCRIPTION_FLOW_CHANGE_EMAIL_SCHEMA_DEFAULT_VALUES,
      },
      resolver: yupResolver(subscriptionFlowChangeEmailFormSchema),
      reValidateMode: "onChange",
    });

  const confirmSubscriptionEmailFormMethods =
    useForm<SubscriptionFlowTokenValidationSchema>({
      defaultValues: {
        ...SUBSCRIPTION_FLOW_TOKEN_VALIDATION_SCHEMA_DEFAULT_VALUES,
      },
      resolver: yupResolver(subscriptionFlowTokenValidationFormSchema),
      reValidateMode: "onChange",
    });

  const changeSubscriptionEmailMutation = useStartChangeSubscriptionEmail();
  const confirmChangeSubscriptionEmailMutation =
    useConfirmChangeSubscriptionEmail();

  const handleStartChangeSubscriptionEmail = useCallback(
    (data: { email: string }) => {
      return changeSubscriptionEmailMutation.mutateAsync({
        subscriptionId,
        email: data.email,
      });
    },
    [subscriptionId, changeSubscriptionEmailMutation]
  );

  const handleConfirmTokenChangeSubscriptionEmail = useCallback(
    async (data: { token: string }) => {
      const response = await confirmChangeSubscriptionEmailMutation.mutateAsync(
        {
          subscriptionId,
          token: data.token,
        }
      );
      changeSubscriptionEmailMutation.reset();
      return response;
    },
    [
      subscriptionId,
      confirmChangeSubscriptionEmailMutation,
      changeSubscriptionEmailMutation,
    ]
  );

  const isTokenStep = changeSubscriptionEmailMutation.status === "success";

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      aria-labelledby="change-email-dialog-title"
      aria-describedby="change-email-dialog-description"
      maxWidth={isTokenStep ? "xs" : "sm"}
      fullWidth={!isTokenStep}
      disableScrollLock
    >
      <DialogTitle>Change e-mail</DialogTitle>
      <DialogContent>
        {!isTokenStep && (
          <FormProvider {...changeSubscriptionEmailFormMethods}>
            <SubscriptionFlowChangeEmailForm
              handleStartChangeSubscriptionEmail={
                handleStartChangeSubscriptionEmail
              }
              status={changeSubscriptionEmailMutation.status}
              onCancel={onClose}
              currentEmail={currentEmail}
            />
          </FormProvider>
        )}

        {isTokenStep && (
          <FormProvider {...confirmSubscriptionEmailFormMethods}>
            <SubscriptionFlowTokenValidationForm
              handleConfirmTokenChangeSubscriptionEmail={
                handleConfirmTokenChangeSubscriptionEmail
              }
              status={confirmChangeSubscriptionEmailMutation.status}
              onCancel={() => {
                onClose();
                changeSubscriptionEmailMutation.reset();
              }}
            />
          </FormProvider>
        )}
      </DialogContent>
    </CustomDialog>
  );
};
